import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  Users,
  Search,
  RefreshCw,
  Tag,
  CheckCircle,
  XCircle,
  PauseCircle,
  X,
  Trash2,
  AlertTriangle
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import apiService from '../../services/api';
import TagManager from './TagManager';
import ContactStatus from './ContactStatus';

const ContactsTab = ({ onError, onSuccess }) => {
  
  const { user } = useAuth();
  // Tema é usado apenas via classes dark: do Tailwind
  
  // Estados locais
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [contacts, setContacts] = useState([]);
  const [importingContacts, setImportingContacts] = useState(false);
  const [importingFile, setImportingFile] = useState(false);
  const [lastLoadTime, setLastLoadTime] = useState(null);
  const [selectedContact, setSelectedContact] = useState(null);
  const [statusFilter, setStatusFilter] = useState('');
  const [tagFilter, setTagFilter] = useState('');
  const [showFilters] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showClearModal, setShowClearModal] = useState(false);
  const [clearingContacts, setClearingContacts] = useState(false);
  
  // Ref para controlar requisições em andamento
  const requestInProgress = useRef(false);
  const lastRequestTime = useRef(0);
  
  // Obter nome do banco de dados do usuário autenticado
  const nomeBaseDados = user?.target_db_name;
  
  // Configurações de cache - dados ficam válidos por 5 minutos
  const DURACAO_CACHE = 5 * 60 * 1000;
  
  // Refs para valores que não devem causar recriação do useCallback
  const onErrorRef = useRef(onError);
  const onSuccessRef = useRef(onSuccess);
  const nomeBaseDadosRef = useRef(nomeBaseDados);
  
  // Atualizar refs quando valores mudarem
  useEffect(() => {
    onErrorRef.current = onError;
  }, [onError]);
  
  useEffect(() => {
    onSuccessRef.current = onSuccess;
  }, [onSuccess]);
  
  useEffect(() => {
    nomeBaseDadosRef.current = nomeBaseDados;
  }, [nomeBaseDados]);

  // Função para formatar data/hora
  const formatDateTime = (dateString) => {
    if (!dateString) return 'Nunca';
    
    try {
      const date = new Date(dateString);
      const now = new Date();
      const diffInHours = (now - date) / (1000 * 60 * 60);
      
      if (diffInHours < 24) {
        return date.toLocaleTimeString('pt-BR', { 
          hour: '2-digit', 
          minute: '2-digit' 
        });
      } else if (diffInHours < 24 * 7) {
        return date.toLocaleDateString('pt-BR', { 
          weekday: 'short',
          hour: '2-digit', 
          minute: '2-digit' 
        });
      } else {
        return date.toLocaleDateString('pt-BR', {
          day: '2-digit',
          month: '2-digit',
          year: '2-digit'
        });
      }
    } catch (error) {
      return 'Data inválida';
    }
  };

  // Debounce para busca
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 300);

    return () => clearTimeout(timer);
  }, [searchTerm]);

  /**
   * Carrega a lista de contatos do CRM com sistema de cache inteligente
   * @param {boolean} forceReload - Se true, ignora o cache e faz nova requisição
   * @param {string} searchTerm - Termo de busca para filtrar contatos
   * @param {string} status - Status para filtrar contatos
   * @param {string} tags - Tags para filtrar contatos
   */
  const carregarContatos = useCallback(async (forceReload = false, searchTerm = '', status = '', tags = '') => {
    // Evitar múltiplas requisições simultâneas
    if (loading || requestInProgress.current) {
      return;
    }

    // Evitar requisições muito rápidas (mínimo 1 segundo entre requisições)
    const now = Date.now();
    if (now - lastRequestTime.current < 1000) {
      return;
    }
    lastRequestTime.current = now;

    // Verificar cache (5 minutos)
    if (!forceReload && lastLoadTime && (Date.now() - lastLoadTime) < DURACAO_CACHE) {
      return;
    }

    // Verificar se o usuário tem um banco de dados configurado
    if (!nomeBaseDadosRef.current) {
      onErrorRef.current('Usuário não tem banco de dados configurado. Entre em contato com o administrador.');
      return;
    }

    try {
      setLoading(true);
      requestInProgress.current = true;
      
      // Carregar todos os contatos usando paginação
      let allContacts = [];
      let currentPage = 1;
      let hasMorePages = true;
      const pageLimit = 1000; // Limite por página

      while (hasMorePages) {
        const parametros = new URLSearchParams({
          page: currentPage,
          limit: pageLimit,
          search: searchTerm,
          status: status,
          tags: tags
        });

        const endpoint = `/crm/contacts?${parametros}`;
        
        const resposta = await apiService.request(endpoint);
        
        if (resposta.success && resposta.data && Array.isArray(resposta.data.contacts)) {
          allContacts = [...allContacts, ...resposta.data.contacts];
          
          // Verificar se há mais páginas
          const { page, pages } = resposta.data.pagination;
          hasMorePages = page < pages;
          currentPage++;
        } else {
          hasMorePages = false;
        }
      }
      
      setContacts(allContacts);
      setLastLoadTime(Date.now());
    } catch (error) {
      
      // Tratamento específico para erro 429 (Too Many Requests)
      if (error.message.includes('429')) {
        onErrorRef.current('Muitas requisições simultâneas. Aguarde um momento e tente novamente.');
      } else {
        onErrorRef.current('Erro ao carregar contatos: ' + error.message);
      }
      setContacts([]);
    } finally {
      setLoading(false);
      requestInProgress.current = false;
    }
  }, [DURACAO_CACHE, lastLoadTime, loading]); // Dependências necessárias

  /**
   * Importa contatos do WhatsApp para o sistema CRM
   * Esta função busca todos os contatos do WhatsApp conectado e os salva no banco de dados
   */
  const importarContatos = async () => {
    if (!nomeBaseDadosRef.current) {
      onErrorRef.current('Selecione um banco de dados primeiro');
      return;
    }

    setImportingContacts(true);

    try {
      const resposta = await apiService.importContactsToWhatsApp(nomeBaseDadosRef.current);
      
      if (resposta.success) {
        // const totalImportados = resposta.imported || 0;
        // const totalIgnorados = resposta.skipped || 0;
        // Notificação removida
        
        // Recarregar lista de contatos para mostrar os novos dados
        await carregarContatos(true);
      } else {
        throw new Error(resposta.message || 'Falha na importação');
      }
    } catch (erro) {
      onErrorRef.current('Erro ao importar contatos: ' + (erro.message || 'Erro desconhecido'));
    } finally {
      setImportingContacts(false);
    }
  };

  // Função para importar contatos de arquivo
  const handleFileImport = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Validar tipo de arquivo
    const allowedTypes = ['.csv', '.docx'];
    const fileExtension = '.' + file.name.split('.').pop().toLowerCase();
    if (!allowedTypes.includes(fileExtension)) {
      onErrorRef.current('Tipo de arquivo não suportado. Use apenas arquivos .csv ou .docx');
      return;
    }

    // Validar tamanho do arquivo (máximo 10MB)
    const maxSize = 10 * 1024 * 1024; // 10MB
    if (file.size > maxSize) {
      onErrorRef.current('Arquivo muito grande. O tamanho máximo é 10MB');
      return;
    }

    if (!nomeBaseDadosRef.current) {
      onErrorRef.current('Nome da base de dados não encontrado');
      return;
    }

    setImportingFile(true);
    
    try {
      const resposta = await apiService.importContactsFromFile(file, nomeBaseDadosRef.current);
      
      if (resposta.success) {
        const { imported = 0, skipped = 0, errors = 0 } = resposta;
        onSuccessRef.current(`Importação concluída: ${imported} contatos importados, ${skipped} ignorados (duplicados), ${errors} erros`);
        
        // Recarregar lista de contatos para mostrar os novos dados
        await carregarContatos(true);
      } else {
        throw new Error(resposta.message || 'Falha na importação do arquivo');
      }
    } catch (erro) {
      onErrorRef.current('Erro ao importar arquivo: ' + (erro.message || 'Erro desconhecido'));
    } finally {
      setImportingFile(false);
      // Limpar o input file para permitir reimportar o mesmo arquivo
      event.target.value = '';
    }
  };

  // Filtrar contatos com debounce (agora feito no backend)
  const filteredContacts = useMemo(() => {
    return contacts;
  }, [contacts]);

  // Função para atualizar contato local
  const handleContactUpdate = (updatedContact) => {
    setContacts(prevContacts => 
      prevContacts.map(contact => 
        contact.id === updatedContact.id ? updatedContact : contact
      )
    );
    
    // Se o contato selecionado foi atualizado, atualizar também
    if (selectedContact && selectedContact.id === updatedContact.id) {
      setSelectedContact(updatedContact);
    }
  };

  // Função para limpar todos os contatos
  const handleClearAllContacts = async () => {
    try {
      setClearingContacts(true);
      
      const response = await apiService.clearAllContacts(nomeBaseDadosRef.current);
      
      if (response.success) {
        setContacts([]);
        setSelectedContact(null);
        onSuccessRef.current('Todos os contatos foram removidos com sucesso!');
        setShowClearModal(false);
      } else {
        throw new Error(response.message || 'Erro ao limpar contatos');
      }
    } catch (erro) {
      onErrorRef.current('Erro ao limpar contatos: ' + (erro.message || 'Erro desconhecido'));
    } finally {
      setClearingContacts(false);
    }
  };

  // Controle para evitar carregamento duplo na inicialização
  const jaCarregouInicialmente = useRef(false);

  // Carregar contatos ao montar o componente (apenas uma vez)
  useEffect(() => {
    if (!jaCarregouInicialmente.current) {
      jaCarregouInicialmente.current = true;
      carregarContatos(false, '', '', '');
    }
    
    // Cleanup para detectar desmontagem
    return () => {
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Sem dependências para executar apenas uma vez

  // Recarregar contatos quando filtros mudarem
  useEffect(() => {
    if (jaCarregouInicialmente.current) {
      const timer = setTimeout(() => {
        carregarContatos(true, debouncedSearchTerm, statusFilter, tagFilter);
      }, 300); // Debounce de 300ms
      
      return () => clearTimeout(timer);
    }
  }, [statusFilter, tagFilter, debouncedSearchTerm, carregarContatos]); // Adicionando carregarContatos nas dependências

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700">
      {/* Header */}
      <div className="p-6 border-b border-gray-200 dark:border-gray-700">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
            Todos os Contatos
          </h2>
          
          <div className="flex space-x-2">
            {/* Botão de Importação */}
            <button
              onClick={importarContatos}
              disabled={importingContacts || !nomeBaseDadosRef.current}
              className="flex items-center px-4 py-2 bg-green-600 hover:bg-green-700 disabled:bg-gray-400 disabled:cursor-not-allowed text-white rounded-lg transition-colors duration-200"
            >
              {importingContacts ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Importando...
                </>
              ) : (
                <>
                  <Users className="w-4 h-4 mr-2" />
                  Importar Contatos
                </>
              )}
            </button>

            {/* Botão de Importar Arquivo */}
            <label className="flex items-center px-4 py-2 bg-purple-600 hover:bg-purple-700 disabled:bg-gray-400 text-white rounded-lg transition-colors duration-200 mr-2 cursor-pointer">
              <input
                type="file"
                accept=".csv,.docx"
                onChange={handleFileImport}
                className="hidden"
                disabled={importingFile}
              />
              {importingFile ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Importando Arquivo...
                </>
              ) : (
                <>
                  <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                  </svg>
                  Importar Arquivo
                </>
              )}
            </label>

            {/* Botão de Atualizar */}
            <button
              onClick={() => carregarContatos(true, debouncedSearchTerm, statusFilter, tagFilter)}
              disabled={loading}
              className="flex items-center px-4 py-2 bg-purple-600 hover:bg-purple-700 disabled:bg-gray-400 disabled:cursor-not-allowed text-white rounded-lg transition-colors duration-200 mr-2"
            >
              <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
              Atualizar
            </button>

            {/* Botão de Limpar Contatos */}
            <button
              onClick={() => setShowClearModal(true)}
              disabled={clearingContacts || contacts.length === 0}
              className="flex items-center px-4 py-2 bg-red-600 hover:bg-red-700 disabled:bg-gray-400 disabled:cursor-not-allowed text-white rounded-lg transition-colors duration-200"
            >
              <Trash2 className="w-4 h-4 mr-2" />
              Limpar Contatos
            </button>
            

          </div>
        </div>

        {/* Busca e filtros */}
        <div className="space-y-3">
          {/* Busca com debounce */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
            <input
              type="text"
              placeholder="Buscar por nome ou telefone..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 dark:bg-gray-700 dark:text-white"
            />
          </div>



          {/* Filtros expandidos */}
          {showFilters && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
              {/* Filtro por status */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Status
                </label>
                <select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 dark:bg-gray-800 dark:text-white"
                >
                  <option value="">Todos os status</option>
                  <option value="ativo">Ativo</option>
                  <option value="ignorado">Ignorado</option>
                  <option value="pausado">Pausado</option>
                </select>
              </div>

              {/* Filtro por tags */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Tags
                </label>
                <input
                  type="text"
                  value={tagFilter}
                  onChange={(e) => setTagFilter(e.target.value)}
                  placeholder="Digite uma tag..."
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 dark:bg-gray-800 dark:text-white"
                />
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Conteúdo */}
      <div className="p-6">
        {loading ? (
          <div className="text-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
            <div className="text-gray-500 dark:text-gray-400">Carregando contatos...</div>
          </div>
        ) : filteredContacts.length === 0 ? (
          <div className="text-center py-8">
            <Users className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            {contacts.length === 0 ? (
              <div>
                <p className="text-gray-500 dark:text-gray-400 mb-4">Nenhum contato encontrado</p>
                <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-6 text-left">
                  <div className="flex items-start space-x-3">
                    <Users className="w-6 h-6 text-blue-600 dark:text-blue-400 mt-1" />
                    <div>
                      <h3 className="text-lg font-semibold text-blue-900 dark:text-blue-100 mb-2">
                        Como importar contatos
                      </h3>
                      <p className="text-blue-800 dark:text-blue-200 mb-3">
                        Para começar a usar o CRM, você precisa importar seus contatos do WhatsApp.
                      </p>
                      <div className="text-sm text-blue-700 dark:text-blue-300">
                        <p className="mb-2"><strong>Como funciona:</strong></p>
                        <ul className="list-disc list-inside space-y-1 ml-4">
                          <li>Busca todos os contatos do seu WhatsApp conectado</li>
                          <li>Salva os contatos na tabela do banco de dados</li>
                          <li>Evita duplicatas - só adiciona contatos novos</li>
                          <li>Formata números de telefone automaticamente</li>
                        </ul>
                        <p className="mt-3 font-medium">
                          Clique no botão "Importar Contatos" para começar.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <p className="text-gray-500 dark:text-gray-400">
                Nenhum contato encontrado para "{debouncedSearchTerm}"
              </p>
            )}
          </div>
        ) : (
          <div className="space-y-2">
            <div className="mb-4 text-sm text-gray-600 dark:text-gray-400">
              Mostrando {filteredContacts.length} de {contacts.length} contatos
              {debouncedSearchTerm && ` para "${debouncedSearchTerm}"`}
            </div>
            
            {/* Lista de contatos com cards limpos */}
            <div className="grid gap-3">
              {filteredContacts.map((contact) => {
                if (!contact || !contact.id) return null;
                
                const statusConfig = {
                  ativo: { icon: CheckCircle, color: 'text-green-600', bgColor: 'bg-green-100' },
                  ignorado: { icon: XCircle, color: 'text-red-600', bgColor: 'bg-red-100' },
                  pausado: { icon: PauseCircle, color: 'text-yellow-600', bgColor: 'bg-yellow-100' }
                };
                
                const currentStatus = contact.status || 'ativo';
                const StatusIcon = statusConfig[currentStatus]?.icon || CheckCircle;
                
                return (
                  <div
                    key={contact.id}
                    onClick={() => setSelectedContact(contact)}
                    className="p-4 border border-gray-200 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors cursor-pointer"
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-3 flex-1">
                        {/* Avatar placeholder */}
                        <div className="w-10 h-10 bg-gray-300 dark:bg-gray-600 rounded-full flex items-center justify-center">
                          <Users className="w-5 h-5 text-gray-600 dark:text-gray-400" />
                        </div>
                        
                        <div className="flex-1">
                          <div className="flex items-center space-x-2">
                            <p className="font-medium text-gray-900 dark:text-white">
                              {contact.nome || 'Nome não informado'}
                            </p>
                            <StatusIcon className={`w-4 h-4 ${statusConfig[currentStatus]?.color || 'text-gray-400'}`} />
                          </div>
                          <p className="text-sm text-gray-500 dark:text-gray-400">
                            {contact.number || 'Telefone não informado'}
                          </p>
                          
                          {/* Tags */}
                          {contact.tags && contact.tags.length > 0 && (
                            <div className="flex flex-wrap gap-1 mt-2">
                              {contact.tags.slice(0, 3).map((tag) => (
                                <span
                                  key={tag}
                                  className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-400"
                                >
                                  <Tag className="w-2 h-2 mr-1" />
                                  {tag}
                                </span>
                              ))}
                              {contact.tags.length > 3 && (
                                <span className="text-xs text-gray-500 dark:text-gray-400">
                                  +{contact.tags.length - 3} mais
                                </span>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                      
                      <div className="flex items-center space-x-2">
                        <span className="text-xs text-gray-500 dark:text-gray-400">
                          {formatDateTime(contact.created_at)}
                        </span>
                        
                        {contact.number && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              const phoneNumber = contact.number.replace(/\D/g, '');
                              window.open(`https://wa.me/${phoneNumber}`, '_blank');
                            }}
                            className="p-2 bg-green-500 hover:bg-green-600 text-white rounded-full transition-colors"
                            title="Abrir WhatsApp"
                          >
                            <svg 
                              className="w-4 h-4" 
                              fill="currentColor" 
                              viewBox="0 0 24 24"
                            >
                              <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893A11.821 11.821 0 0020.885 3.515z"/>
                            </svg>
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>

      {/* Modal de detalhes do contato */}
      {selectedContact && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 animate-fadeIn">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto transform transition-all duration-300 animate-slideInUp">
            {/* Header do modal */}
            <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
              <div className="flex items-center space-x-3">
                <div className="w-12 h-12 bg-gray-300 dark:bg-gray-600 rounded-full flex items-center justify-center">
                  <Users className="w-6 h-6 text-gray-600 dark:text-gray-400" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                    {selectedContact.nome || 'Nome não informado'}
                  </h3>
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    {selectedContact.number || 'Telefone não informado'}
                  </p>
                </div>
              </div>
              
              <button
                onClick={() => setSelectedContact(null)}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            {/* Conteúdo do modal */}
            <div className="p-6 space-y-6">
              {/* Informações básicas */}
              <div>
                <h4 className="text-sm font-medium text-gray-900 dark:text-white mb-3">
                  Informações do Contato
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-gray-500 dark:text-gray-400">Nome:</span>
                    <p className="font-medium text-gray-900 dark:text-white">
                      {selectedContact.nome || 'Não informado'}
                    </p>
                  </div>
                  <div>
                    <span className="text-gray-500 dark:text-gray-400">Telefone:</span>
                    <p className="font-medium text-gray-900 dark:text-white">
                      {selectedContact.number || 'Não informado'}
                    </p>
                  </div>
                  <div>
                    <span className="text-gray-500 dark:text-gray-400">Criado em:</span>
                    <p className="font-medium text-gray-900 dark:text-white">
                      {formatDateTime(selectedContact.created_at)}
                    </p>
                  </div>
                  <div>
                    <span className="text-gray-500 dark:text-gray-400">ID:</span>
                    <p className="font-medium text-gray-900 dark:text-white font-mono text-xs">
                      {selectedContact.id}
                    </p>
                  </div>
                </div>
              </div>

              {/* Status do contato */}
              <div className="border-t border-gray-200 dark:border-gray-700 pt-6">
                <ContactStatus
                  contact={selectedContact}
                  onContactUpdate={handleContactUpdate}
                  onError={onError}
                  onSuccess={onSuccess}
                />
              </div>

              {/* Tags do contato */}
              <div className="border-t border-gray-200 dark:border-gray-700 pt-6">
                <TagManager
                  contact={selectedContact}
                  onContactUpdate={handleContactUpdate}
                  onError={onError}
                  onSuccess={onSuccess}
                />
              </div>

              {/* Ações */}
              <div className="border-t border-gray-200 dark:border-gray-700 pt-6">
                <h4 className="text-sm font-medium text-gray-900 dark:text-white mb-3">
                  Ações
                </h4>
                <div className="flex space-x-3">
                  {selectedContact.number && (
                    <button
                      onClick={() => {
                        const phoneNumber = selectedContact.number.replace(/\D/g, '');
                        window.open(`https://wa.me/${phoneNumber}`, '_blank');
                      }}
                      className="flex items-center px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg transition-colors"
                    >
                      <svg className="w-4 h-4 mr-2" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893A11.821 11.821 0 0020.885 3.515z"/>
                      </svg>
                      Abrir WhatsApp
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Confirmação para Limpar Contatos */}
      {showClearModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-md w-full mx-4">
            <div className="flex items-center mb-4">
              <AlertTriangle className="w-6 h-6 text-red-500 mr-3" />
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                Confirmar Exclusão
              </h3>
            </div>
            
            <div className="mb-6">
              <p className="text-gray-700 dark:text-gray-300 mb-3">
                Tem certeza que deseja excluir <strong>todos os contatos</strong>?
              </p>
              <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-3">
                <p className="text-red-800 dark:text-red-200 text-sm font-medium mb-2">
                  ⚠️ Esta operação é irreversível!
                </p>
                <ul className="text-red-700 dark:text-red-300 text-sm space-y-1">
                  <li>• Todos os contatos serão removidos permanentemente</li>
                  <li>• Os status dos contatos que foram enviados serão perdidos</li>
                  <li>• Não será possível recuperar essas informações</li>
                </ul>
              </div>
            </div>
            
            <div className="flex justify-end space-x-3">
              <button
                onClick={() => setShowClearModal(false)}
                disabled={clearingContacts}
                className="px-4 py-2 text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-lg transition-colors duration-200 disabled:opacity-50"
              >
                Cancelar
              </button>
              <button
                onClick={handleClearAllContacts}
                disabled={clearingContacts}
                className="flex items-center px-4 py-2 bg-red-600 hover:bg-red-700 disabled:bg-red-400 text-white rounded-lg transition-colors duration-200"
              >
                {clearingContacts ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Excluindo...
                  </>
                ) : (
                  <>
                    <Trash2 className="w-4 h-4 mr-2" />
                    Sim, Excluir Todos
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ContactsTab;